import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from '@emotion/styled'
import { Box, Card, Typography } from '@mui/material'
import { Container } from '@mui/system'
import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  RowClickedEvent,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community'
import { PayoutContractStatus } from '@archax/shared-types'
import { getPayouts } from '../../api/payouts'
import { formatUTCMMDDYYYY } from '../../util/formatters'
import { dateFilterOptions, substringFilterParams } from '../../util/common-grid-options'
import { renderPayoutStatus } from './components/Status'
import { ServerSideGrid } from '../../components/ServerSideGrid'
import { AddButton } from '../../components/ui/AddButton'
import Dialog from '../../components/Dialog/Dialog'
import CreateForm from './components/CreateForm'

const StyledTitle = styled(Typography)(() => ({
  display: 'flex',
  flexDirection: 'column',
  small: {
    fontSize: '0.7em',
    fontWeight: 'normal',
  },
}))

export default function ListPayouts() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const columnDefs: ColDef<any>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('payoutContracts.labels.name'),
        minWidth: 400,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: substringFilterParams,
      },
      {
        field: 'start_at',
        headerName: t('payoutContracts.labels.startDate'),
        valueGetter: (params: ValueGetterParams<any>) => {
          return formatUTCMMDDYYYY(params.data?.startAt)
        },
        minWidth: 140,
        flex: 2,
        sortable: true,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterOptions,
      },
      {
        field: 'end_at',
        headerName: t('payoutContracts.labels.endDate'),
        valueGetter: (params: ValueGetterParams<any>) => {
          return formatUTCMMDDYYYY(params.data?.endAt)
        },
        minWidth: 140,
        flex: 2,
        sortable: true,
        filter: 'agTimeColumnFilter',
        filterParams: dateFilterOptions,
      },
      {
        field: 'status',
        headerName: t('payoutContracts.labels.status'),
        sortable: true,
        cellRenderer: (params: ICellRendererParams<any>) => {
          return renderPayoutStatus(params.data?.status)
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: Object.values(PayoutContractStatus),
          valueFormatter: (params: ValueFormatterParams<any>) => params.value,
        },
      },
    ],
    [t],
  )

  const gridOptions: GridOptions = useMemo(
    () => ({
      columnDefs,
      floatingFilter: true,
      defaultColDef: { flex: 1 },
      onRowClicked: (params: RowClickedEvent<any>) => {
        navigate(`/payouts/${params.data!.id}`)
      },
      onGridReady: (params) => {
        params.api?.sizeColumnsToFit()
      },
    }),
    [navigate, columnDefs],
  )

  return (
    <Container data-testid="list-payout-contracts" maxWidth="xl">
      <Card sx={{ p: 7 }}>
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <StyledTitle data-testid="list-payout-contracts__title" align="left" variant="h3">
            {t('payoutContracts.listAll.title')}
            <small>{t('payoutContracts.listAll.subtitle')}</small>
          </StyledTitle>
          <AddButton
            name={t('payoutContracts.actions.createNewContract')}
            ariaLabel={t('payoutContracts.actions.createNewContract')}
            onClick={() => setShowCreateDialog(true)}
          />
        </Box>
        <Box>
          <ServerSideGrid
            gridOptions={gridOptions}
            queryFn={getPayouts}
            queryParamsEnabled
            showOverlayLoading
            showRefreshIcon
          />
        </Box>
      </Card>
      <Dialog
        title={t('payoutContracts.actions.createNewContract')}
        onClose={() => setShowCreateDialog(false)}
        open={showCreateDialog}
      >
        <CreateForm onCancel={() => setShowCreateDialog(false)} onSuccess={() => setShowCreateDialog(false)} />
      </Dialog>
    </Container>
  )
}
